import { useMemo, useState } from "react";
import clsx from "clsx";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { toast } from "@/lib/toast";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
  patchProjectWorkflowStatus,
  projectHasCommissionToRevoke,
  projectRejectSuccessMessage,
  projectWorkflowStatus,
  projectWorkflowStatusBadgeClass,
  rejectProject,
  type Order,
  type ProjectRejectPayload,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";
import {
  ProjectCommissionRevokeDialog,
  type CommissionRevokeConfirm,
} from "@/projectOverview/ProjectCommissionRevokeDialog";
import { ProjectRejectDialog } from "@/projectOverview/ProjectRejectDialog";
import {
  ProjectWorkflowStatusDialog,
  ProjectWorkflowStatusPickerDialog,
  type ProjectWorkflowStatusChange,
} from "@/projectOverview/ProjectWorkflowStatusDialog";

const AUTO_VALUE = "__auto__";

export function ProjectWorkflowStatusControl({
  project,
  canEdit,
  showBadges = true,
  showManualLabel = true,
  selectLabel,
  variant = "select",
  onSaved,
}: {
  project: Order;
  canEdit: boolean;
  showBadges?: boolean;
  /** Kurzer Hinweis „manuell“, wenn die Badges ausgeblendet sind. */
  showManualLabel?: boolean;
  selectLabel?: string;
  /** `badge`: Status-Badge öffnet ein kleines Modal. `select`: Auswahl direkt daneben. */
  variant?: "select" | "badge";
  onSaved?: () => void;
}) {
  const workflow = projectWorkflowStatus(project);
  const isManual = Boolean(project.workflow_status_manual);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<ProjectWorkflowStatusChange | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revokeStatus, setRevokeStatus] = useState<"angeboten" | "offen" | null>(null);
  const revokeCommission = projectHasCommissionToRevoke(project);

  function openRejectDialog() {
    setPending(null);
    setPickerOpen(false);
    setRevokeStatus(null);
    setRejectOpen(true);
  }

  function openCommissionRevoke(status: "angeboten" | "offen") {
    setPending(null);
    setPickerOpen(false);
    setRejectOpen(false);
    setRevokeStatus(status);
  }

  const selectValue = pending
    ? pending.kind === "auto"
      ? AUTO_VALUE
      : pending.status
    : workflow.key;

  const dialogCopy = useMemo(() => {
    if (!pending) {
      return {
        title: "Projektstatus ändern",
        description: "",
        confirmLabel: "Status speichern",
      };
    }
    if (pending.kind === "auto") {
      return {
        title: "Status automatisch ableiten",
        description:
          "Der aktuelle manuelle Status wird aufgehoben. Der neue Status ergibt sich aus Nachweis, Beauftragung und Abrechnung. Die Begründung wird mit Person und Zeitpunkt in die Projektnotizen geschrieben.",
        confirmLabel: "Automatisch ableiten",
      };
    }
    const fromLabel = workflow.label;
    const toLabel = PROJECT_WORKFLOW_STATUS_LABELS[pending.status];
    return {
      title: "Projektstatus ändern",
      description: `Status von „${fromLabel}“ auf „${toLabel}“ setzen? Die Begründung wird mit Person und Zeitpunkt in die Projektnotizen geschrieben.`,
      confirmLabel: "Status speichern",
    };
  }, [pending, workflow.label]);

  async function applyChange(change: ProjectWorkflowStatusChange, begruendung: string) {
    setBusy(true);
    try {
      await patchProjectWorkflowStatus(project.id, {
        workflow_status: change.kind === "set" ? change.status : undefined,
        enabled: change.kind === "set",
        begruendung,
      });
      toast.success("Projektstatus aktualisiert.");
      setPending(null);
      setPickerOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Status konnte nicht geändert werden.");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(begruendung: string) {
    if (!pending) return;
    await applyChange(pending, begruendung);
  }

  async function handleCommissionRevoke(payload: CommissionRevokeConfirm) {
    if (!revokeStatus) return;
    setBusy(true);
    try {
      await patchProjectWorkflowStatus(project.id, {
        workflow_status: revokeStatus,
        enabled: true,
        begruendung: payload.begruendung,
        beauftragung_zuruecknehmen: true,
        notiz: payload.notiz.trim() || undefined,
      });
      toast.success("Beauftragung zurückgenommen.");
      setRevokeStatus(null);
      setPickerOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Beauftragung konnte nicht zurückgenommen werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleReject(payload: ProjectRejectPayload) {
    setBusy(true);
    try {
      const result = await rejectProject(project.id, payload);
      toast.success(projectRejectSuccessMessage(result));
      setRejectOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  const revokeDialog = (
    <ProjectCommissionRevokeDialog
      open={revokeStatus != null}
      projectId={project.id}
      targetStatus={revokeStatus}
      loading={busy}
      onClose={() => {
        if (!busy) setRevokeStatus(null);
      }}
      onConfirm={(payload) => void handleCommissionRevoke(payload)}
    />
  );

  const rejectDialog = (
    <ProjectRejectDialog
      open={rejectOpen}
      projectId={project.id}
      auftragsnummer={project.auftragsnummer}
      loading={busy}
      onClose={() => {
        if (!busy) setRejectOpen(false);
      }}
      onConfirm={(payload) => void handleReject(payload)}
    />
  );

  if (variant === "badge") {
    return (
      <>
        {canEdit ? (
          <button
            type="button"
            className={clsx("eh-po-info-status-btn text-xs", projectWorkflowStatusBadgeClass(workflow.key))}
            title="Status ändern"
            aria-haspopup="dialog"
            disabled={busy}
            onClick={() => setPickerOpen(true)}
          >
            {workflow.label}
          </button>
        ) : (
          <span className={clsx("text-xs", projectWorkflowStatusBadgeClass(workflow.key))}>
            {workflow.label}
          </span>
        )}
        <ProjectWorkflowStatusPickerDialog
          open={pickerOpen}
          currentKey={workflow.key}
          currentLabel={workflow.label}
          isManual={isManual}
          loading={busy}
          onClose={() => {
            if (!busy) setPickerOpen(false);
          }}
          onConfirm={(change, grund) => void applyChange(change, grund)}
          onChooseAbgelehnt={openRejectDialog}
          onChooseCommissionRevoke={revokeCommission ? openCommissionRevoke : undefined}
        />
        {revokeDialog}
        {rejectDialog}
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showBadges ? (
        <span className="eh-wf-badge-stack items-start">
          <span className={clsx("text-xs", projectWorkflowStatusBadgeClass(workflow.key))}>
            {workflow.label}
          </span>
          {workflow.secondary ? (
            <span
              className={clsx(
                "text-xs",
                projectWorkflowStatusBadgeClass(workflow.secondary.key),
              )}
            >
              {workflow.secondary.label}
            </span>
          ) : null}
          {isManual ? (
            <span className="text-[11px] font-medium text-label-tertiary">manuell</span>
          ) : null}
        </span>
      ) : showManualLabel && isManual ? (
        <span className="text-[11px] font-medium text-label-tertiary">manuell</span>
      ) : null}
      {canEdit ? (
        <label className="inline-flex items-center gap-2">
          {selectLabel ? (
            <span className="text-xs font-medium text-label-tertiary">{selectLabel}</span>
          ) : null}
          <NativeSelect
            aria-label={selectLabel || "Projektstatus ändern"}
            selectSize="sm"
            disabled={busy}
            value={selectValue}
            onChange={(event) => {
              const next = event.target.value;
              if (next === AUTO_VALUE) {
                setPending({ kind: "auto" });
                return;
              }
              if (next === "abgelehnt" && workflow.key !== "abgelehnt") {
                openRejectDialog();
                return;
              }
              if (
                (next === "angeboten" || next === "offen") &&
                next !== workflow.key &&
                revokeCommission
              ) {
                openCommissionRevoke(next);
                return;
              }
              if (next === workflow.key) {
                setPending(null);
                return;
              }
              setPending({ kind: "set", status: next as ProjectWorkflowStatusKey });
            }}
          >
            {isManual ? <option value={AUTO_VALUE}>Automatisch ableiten</option> : null}
            {PROJECT_WORKFLOW_STATUSES.map((key) => (
              <option key={key} value={key}>
                {PROJECT_WORKFLOW_STATUS_LABELS[key]}
              </option>
            ))}
          </NativeSelect>
        </label>
      ) : null}
      <ProjectWorkflowStatusDialog
        open={pending != null}
        title={dialogCopy.title}
        description={dialogCopy.description}
        confirmLabel={dialogCopy.confirmLabel}
        loading={busy}
        onClose={() => {
          if (!busy) setPending(null);
        }}
        onConfirm={(grund) => void handleConfirm(grund)}
      />
      {revokeDialog}
      {rejectDialog}
    </div>
  );
}
